import { BehaviorSubject } from "rxjs";
import _ from "lodash/fp";

import { permissionsInclude } from "../../services/utils";

const initialValue = window?.sessionStorage.getItem("workspace");

const subject = new BehaviorSubject(
  initialValue
    ? JSON.parse(initialValue)
    : {
        cards: [],
        cols: 36,
        contentEditorsState: { showBlocks: false, showCharacters: false },
      },
);

const store = {
  closeCard: (name) => () => {
    const current = subject.getValue();
    subject.next({
      ...current,
      cards: current.cards.filter((card) => card.i !== name),
    });
  },
  /**
   * Gets the current value of the stream
   */
  get: () => {
    return subject.getValue();
  },
  /**
   * Initializes the stream with default values
   */
  onBreakpointChange: (newBreakpoint, newCols) => {
    console.debug("Breakpoint changed: %s, cols: %d", newBreakpoint, newCols);
  },
  onLayoutChange: (currentLayout, allLayouts) => {
    console.debug(
      "Layout changed: %o, allLayouts: %o",
      currentLayout,
      allLayouts,
    );
    const current = subject.getValue();
    subject.next({
      ...current,
      cards: currentLayout.map((o) => {
        const card = current.cards.find((c) => c.i === o.i);
        if (card) return { ...card, ...o };
        return { ...o };
      }),
    });
  },
  onWidthChange: (_containerWidth, _margin, columns, _containerPadding) => {
    const currentValue = store.get();
    if (columns !== currentValue.cols)
      store.set({
        ...currentValue,
        cols: columns,
      });
  },
  openCard: (name, item, passage, { useMinHeight } = {}) => {
    console.debug("Opening card: %s", name);
    const current = subject.getValue();
    subject.next({
      ...current,
      cards: [
        ...current.cards,
        getCard(current.cols)(current.cards)(name, item, passage, {
          useMinHeight,
        }),
      ],
    });
  },
  selectTab: (name) => (_e, tab) => {
    const current = subject.getValue();
    const next = {
      ...current,
      cards: current.cards.reduce((acc, card) => {
        if (card.i === name)
          return [
            ...acc,
            { ...card, state: { ...card.state, selectedTab: tab } },
          ];
        return [...acc, card];
      }, []),
    };
    subject.next(next);
  },
  /**
   * Write data to the stream
   */
  set: (value) => {
    console.debug("Setting workspace stream: %o", value);
    subject.next(value);
  },
  setDisplay: (name) => (_e, display) => {
    const current = subject.getValue();
    const next = {
      ...current,
      cards: current.cards.reduce((acc, card) => {
        if (card.i === name)
          return [...acc, { ...card, state: { ...card.state, display } }];
        return [...acc, card];
      }, []),
    };
    subject.next(next);
  },
  setLayout: ({ activeTasks, item, layout, passage, useMinHeight = false }) => {
    const current = subject.getValue();
    let cards;

    if (isArtTask(activeTasks)) {
      cards = getDefaultArtTaskLayout(cols.lg, item, passage, activeTasks);
    } else {
      cards =
        layout ||
        getDefaultLayout(cols.lg, item, passage, activeTasks, {
          useMinHeight,
        });
    }

    subject.next({
      ...current,
      cards,
    });
  },
  setOrientation: (name) => (_e, orientation) => {
    const current = subject.getValue();
    const next = {
      ...current,
      cards: current.cards.reduce((acc, card) => {
        if (card.i === name)
          return [...acc, { ...card, state: { ...card.state, orientation } }];
        return [...acc, card];
      }, []),
    };
    subject.next(next);
  },
  /**
   * Subscribe to the stream
   *
   * @returns {Function} A function to unsubscribe from the stream
   */
  subscribe: (setState) => {
    return subject.subscribe(setState);
  },
};

const breakpoints = {
  lg: 1200,
};

const cols = { lg: 36 };

const defaultCardWidth = 0.5;

const getCard =
  (columns = 36) =>
  (layout = []) =>
  (name, item, passage, { useMinHeight } = {}) => {
    const card = cards[name];
    if (!card) return null;

    return {
      state: {},
      ...card,
      i: name,
      ...getCardDimensions(columns)(card, item, passage, name, {
        useMinHeight,
      }),
      ...getCardPosition(columns)(layout),
    };
  };

const DEFAULT_GRID_ROWS = 14;

const fullHeightCards = [
  "ItemContentCard",
  "ItemMetadataCard",
  "PassageContentCard",
  "PassageMetadataCard",
];

const getCardDimensions =
  (columns = 36) =>
  (card, item, passage, name, { useMinHeight } = {}) => {
    const defaultH = useMinHeight
      ? 4
      : fullHeightCards.includes(name)
        ? DEFAULT_GRID_ROWS
        : DEFAULT_GRID_ROWS /
          Math.max([item, passage].filter(Boolean).length, 1);
    const defaults = {
      h: defaultH,
      minH: 4,
      minW: Math.floor((columns * defaultCardWidth) / 3),
      static: false,
      w: Math.floor(columns * defaultCardWidth),
    };
    return ["h", "minH", "minW", "w", "static"].reduce((acc, key) => {
      switch (key) {
        case "static":
          if (card[key]) {
            return {
              ...acc,
              minH: acc.h,
              minW: acc.w,
              [key]: card[key],
            };
          } else {
            return {
              ...acc,
              [key]: false,
            };
          }
        default:
          if (card[key]) {
            return {
              ...acc,
              [key]:
                typeof card[key] === "number" ? card[key] : card[key](columns),
            };
          } else {
            return { ...acc, [key]: defaults[key] };
          }
      }
    }, {});
  };

const getCardPosition =
  (columns = 36) =>
  (layout) => {
    const columnHeights = getColumnHeights(columns)(layout);
    const chunks = _.pipe([
      _.chunk(Math.floor(columns * defaultCardWidth)),
      _.map(_.max),
    ])(columnHeights);
    const y = Math.min(...chunks);
    const x = Math.ceil(
      chunks.findIndex((v) => v === y) * columns * defaultCardWidth,
    );
    return { x, y };
  };

const getColumnHeights =
  (columns = 36) =>
  (layout) => {
    if (layout.length === 0) return Array(columns).fill(0);
    return [...layout]
      .sort((a, b) => {
        return a.y + a.h - (b.y + b.h);
      })
      .reduce((acc, { h, w, x, y }) => {
        return [...acc.fill(y + h, x, x + w)];
      }, Array(columns));
  };

const isArtTask = (activeTasks) => {
  const itemRaw = activeTasks?.item;
  const passageRaw = activeTasks?.passage;
  const itemTasks = Array.isArray(itemRaw) ? itemRaw : itemRaw ? [itemRaw] : [];
  const passageTasks = Array.isArray(passageRaw)
    ? passageRaw
    : passageRaw
      ? [passageRaw]
      : [];
  const firstTask = itemTasks.length
    ? itemTasks[0]
    : passageTasks.length
      ? passageTasks[0]
      : null;

  const artTaskNames = ["create art", "edit art", "upload art"];
  return (
    firstTask && artTaskNames.includes((firstTask.name || "").toLowerCase())
  );
};

const getDefaultArtTaskLayout = (columns, item, passage, activeTasks) => {
  const itemRaw = activeTasks?.item;
  const passageRaw = activeTasks?.passage;
  const itemTasks = Array.isArray(itemRaw) ? itemRaw : itemRaw ? [itemRaw] : [];
  const passageTasks = Array.isArray(passageRaw)
    ? passageRaw
    : passageRaw
      ? [passageRaw]
      : [];
  const firstTask = itemTasks.length
    ? itemTasks[0]
    : passageTasks.length
      ? passageTasks[0]
      : null;

  const type =
    (firstTask.type && firstTask.type.toLowerCase()) ||
    (item ? "item" : passage ? "passage" : "item");
  const taskCard = type === "item" ? "ItemTasksCard" : "PassageTasksCard";
  const contentCard =
    type === "item" ? "ItemContentCard" : "PassageContentCard";
  const assetsCard = type === "item" ? "ItemAssetsCard" : "PassageAssetsCard";

  const layoutCardNames = ["CardList", contentCard, assetsCard, taskCard];

  return layoutCardNames.filter(Boolean).reduce((cardsAcc, name) => {
    const card = getCard(columns)(cardsAcc)(name, item, passage);
    return [...cardsAcc, card];
  }, []);
};

const getDefaultLayout = (
  columns,
  item,
  passage,
  activeTasks,
  { useMinHeight } = {},
) => {
  if (useMinHeight) {
    return [
      "CardList",
      passage && "PassageContentCard",
      item && "ItemContentCard",
      item && "ItemMetadataCard",
      activeTasks.item && "ItemTasksCard",
      activeTasks.passage && "PassageTasksCard",
      item && "ItemPreviewCard",
      passage && "ItemSetSummaryCard",
    ]
      .filter(Boolean)
      .reduce((cardsAcc, name) => {
        const card = getCard(columns)(cardsAcc)(name, item, passage, {
          useMinHeight,
        });
        return [...cardsAcc, card];
      }, []);
  }

  return [
    "CardList",
    passage &&
      !permissionsInclude({ activeTasks, type: "passage" })(
        "noPassageContent",
      ) &&
      "PassageContentCard",
    item &&
      !permissionsInclude({ activeTasks, type: "item" })("noItemContent") &&
      "ItemContentCard",
    passage &&
      permissionsInclude({ activeTasks, type: "passage" })(
        "noPassageContent",
      ) &&
      "PassagePreviewCard",
    item &&
      permissionsInclude({ activeTasks, type: "item" })("noItemContent") &&
      "ItemPreviewCard",
    passage &&
      !permissionsInclude({ activeTasks, type: "passage" })(
        "noPassageMetadata",
      ) &&
      "PassageMetadataCard",
    passage &&
      permissionsInclude({ activeTasks, type: "passage" })(
        "noPassageMetadata",
      ) &&
      "ItemSetSummaryCard",
    item && "ItemMetadataCard",
    activeTasks.passage && "PassageTasksCard",
    activeTasks.item && "ItemTasksCard",
  ]
    .filter(Boolean)
    .reduce((cardsAcc, name) => {
      const card = getCard(columns)(cardsAcc)(name, item, passage, {
        useMinHeight,
      });
      return [...cardsAcc, card];
    }, []);
};

const cards = {
  CardList: {
    h: 1,
    resizeHandles: [],
    static: true,
    w: (cols) => cols,
    x: 0,
    y: 0,
  },
  ItemAssetsCard: {
    state: {
      selectedTab: "Images",
    },
  },
  ViewItemImageAssetsCard: {
    state: {
      selectedTab: "Images",
    },
  },
  ItemContentCard: {
    state: {
      orientation: "left-right",
      selectedTab: "Content",
    },
  },
  ItemPreviewCard: {
    state: {
      display: "CBT",
    },
  },
  ItemMetadataCard: {
    state: {
      selectedTab: "General",
    },
  },
  ItemSetSummaryCard: {},
  ItemTasksCard: {},
  PassageAssetsCard: {
    state: {
      selectedTab: "Images",
    },
  },
  PassageContentCard: {
    state: {
      orientation: "left-right",
      selectedTab: "Content",
    },
  },
  PassagePreviewCard: {
    state: {
      display: "CBT",
    },
  },
  PassageMetadataCard: {
    state: {
      selectedTab: "General",
    },
  },
  ViewPassageImageAssetsCard: {
    state: {
      selectedTab: "Images",
    },
  },
  PassageReferenceContentCard: {},
  PassageTasksCard: {},
};

const isEla = (contentArea) =>
  contentArea === "English" || contentArea === "Reading";

const getElaMinHeight = ({ item, itemID, passage, passageID, user }) => {
  if (itemID && item?.id !== itemID) return false;
  if (passageID && passage?.id !== passageID) return false;

  const programId = item?.program?.id || passage?.program?.id;
  const isContentSpecialist = user?.programAccess?.some(
    (p) =>
      p.program === programId && p.reviewTypes?.includes("Content Specialist"),
  );
  if (!isContentSpecialist) return false;

  return isEla(item?.contentArea) || isEla(passage?.contentArea);
};

export {
  breakpoints,
  cols,
  DEFAULT_GRID_ROWS,
  getColumnHeights,
  getCard,
  getCardDimensions,
  getCardPosition,
  getDefaultLayout,
  getDefaultArtTaskLayout,
  getElaMinHeight,
  isArtTask,
  store,
};
